<script>
import VueCal from 'vue-cal'
import 'vue-cal/dist/vuecal.css'
import ProSelect from '$common/ProSelect'
import ProInput from '$common/ProInput'

export default {
  name: 'AdminClassEditor',
  components: {
    'vue-cal': VueCal,
    'pro-select': ProSelect,
    'pro-input': ProInput,
  },
  data () {
    return {
      classes: [],
      saving: false,
      previouslySavedClassString: null,
      classGroups: [],
      locations: [],
      fullTimes: {
        start_time: {},
        end_time: {},
      },
      newClass: {
        location_id: '',
        class_group_id: '',
        start_time: '',
        end_time: '',
        date: '',
      },
    }
  },
  computed: {
    locationOptions () {
      return this.locations.map(location => ({
        value: location.id,
        name: location.display_name,
      }))
    },
    classGroupOptions () {
      return this.classGroups.map(classGroup => ({
        value: classGroup.id,
        name: classGroup.name,
      }))
    },
    readyToAddClass () {
      const nc = this.newClass
      return nc.location_id && nc.class_group_id && nc.start_time && nc.end_time && nc.date && !this.saving
    },
    addClassButtonCopy () {
      const nc = this.newClass
      if (!nc.location_id) return 'Choose location'
      if (!nc.class_group_id) return 'Choose class group'
      if (!nc.start_time) return 'Choose start time'
      if (!nc.end_time) return 'Choose end time'
      if (!nc.date) return 'Choose date'
      if (this.saving) return 'Saving...'
      const d = nc.date
      return `Add Class to the ${this.$buildDate(d, '{shortDay} {date}{dateEnding}, {year}')}`
    },
    eventsFromClasses () {

      return this.classes.map(_class => {
        const stringDate = this.$buildDate(_class.date, '{year}-{month}-{date}')
        return {
          start: `${stringDate} ${_class.start_time}`,
          end: `${stringDate} ${_class.end_time}`,
          class: `${_class.location_id} ${_class.status}`,
          title: `${this.getClassGroupName(_class.class_group_id)}`,
          id: _class.id,
        }
      })
    },
  },
  methods: {
    getClassGroupName (classGroupId) {
      const classGroup = this.classGroups.find(cg => cg.id === classGroupId)
      return classGroup && classGroup.short_name
    },
    getClassOptions () {
      const entities = { classGroups: 'class_groups', locations: 'locations'}

      Object.keys(entities).forEach(async entityKey => {
        this[entityKey] = (await this.$firestore.collection(entities[entityKey]).get())
          .docs.map(doc => doc.data())
      })
    },
    getTimeOptions (timeType) {
      return {
        title: {start_time: 'Start time', end_time: 'End time'}[timeType],
        onFocus: () => {
          this.$modals.show({
            name: 'clock-picker',
            onSave: (time) => {
              this.fullTimes[timeType] = time
              this.newClass[timeType] = time.formatted
              this.$modals.hide('clock-picker')
            },
          })
        },
      }
    },
    onCellClick (date) {
      if (!date) return
      this.newClass.date = date.setHours(12, 0, 0, 0)
    },
    onEventClick (e) {
      this.onCellClick(e.startDate) //makes sure that the selected date changes even if it was an event that was clicked
      const clickedClass = this.classes.find(_class => _class.id === e.id)
      this.$modals.show({
        name: 'class-editor',
        classToEdit: this.$clone(clickedClass),
        locations: this.locationOptions,
        classGroups: this.classGroupOptions,
      })
    },
    async onAddClass () {
      if (!this.readyToAddClass) return this.$toast(this.addClassButtonCopy)
      this.saving = true

      const newClassFormatted = {
        ...this.newClass,
        status: 'OPEN',
      }
      //puts in actual times instead of display times
      ;['start_time', 'end_time'].forEach(timeType => {
        const ft = this.fullTimes[timeType]
        newClassFormatted[timeType] = `${ft.hours}:${this.$ensure2(ft.minutes)}`
      })

      //avoids accidental double saving
      const classString = JSON.stringify(newClassFormatted)
      if (this.previouslySavedClassString === classString) {
        this.saving = false
        return this.$toast('You must change details before saving another class')
      }

      const classesRef = this.$firestore.collection('classes')
      const classId = classesRef.doc().id
      newClassFormatted['id'] = classId

      try {
        await classesRef.doc(classId).set(newClassFormatted)
        this.$toast('Save successful')
        this.previouslySavedClassString = classString
      } catch (e) {
        console.log(e)
        this.$toast('Error occurred')
      }

      this.saving = false
    },
    startClassesListener () {
      this.$firestore.collection('classes').onSnapshot(ds => {
        this.classes = ds.docs.map(doc => doc.data())
      })
    },
  },
  mounted () {
    this.getClassOptions()
    this.startClassesListener()
  },
}
</script>

<template lang="pug">
  .class-editor-main.admin-main
    .class-editor-container.admin-container
      .admin-title Classes
      .admin-content
        .add-class-holder
          .options-holder
            .pro-form
              .form-row
                pro-select(
                  :options='locationOptions'
                  v-model='newClass.location_id'
                  title='Location'
                )
                pro-select(
                  :options='classGroupOptions'
                  v-model='newClass.class_group_id'
                  title='Class Group'
                )
              .form-row
                pro-input(
                  v-for=`(time, idx) in ['start_time', 'end_time']`
                  :key='idx'
                  v-model='newClass[time]'
                  :options=`getTimeOptions(time)`
                )
          .add-button-holder
            .add-button(
              :class=`readyToAddClass ? 'ready' : ''`
              @click='onAddClass'
            ) {{addClassButtonCopy}}
        .location-key-holder
          .location-key
            .location(
              v-for='location in locations'
              :class='location.id'
            )
              .name {{location.display_name}}

        .calendar-holder
          vue-cal(
            default-view='month'
            :disable-views=`['day', 'year', 'years']`
            hide-weekends
            :time-from='60 * 15'
            :time-to='60 * 22'
            twelve-hour
            @cell-click='onCellClick'
            :on-event-click='onEventClick'
            :events='eventsFromClasses'
            :events-on-month-view=`'short'`
          )


</template>

<style lang="sass" scoped>
  // vuecal styling is in $styles/overwrite-vuecal.sass
  // @import '$styles/admin.sass'
  @import '$styles/form.sass'
  .class-editor-main
    .class-editor-container
      .admin-content
        .add-class-holder
          display: grid
          grid-template-columns: minmax(300px, 600px) auto
          max-width: 1000px
          .options-holder
            .pro-form
              max-width: 600px
              .form-row
                grid-template-columns: 1fr 1fr
                margin-bottom: 10px
                > *
                  padding-right: 25px
          .add-button-holder
            display: grid
            align-items: center
            justify-items: center
            .add-button
              padding: 30px
              width: 150px
              background-color: #DDD
              color: #444
              text-align: center
              border-radius: 5px
              transition: all .25s
              cursor: not-allowed
              &.ready
                background-color: $orange
                color: white
                font-weight: bold
                cursor: pointer
        .location-key-holder
          .location-key
            display: flex
            .location
              display: grid
              grid-template-columns: auto auto
              margin-right: 15px
              .name
                padding: 5px 10px
                font-size: 14px
                color: white
        .calendar-holder
          max-width: 1000px
          margin-top: 20px
          padding-right: 50px
</style>